import { useParams, Link } from "react-router-dom";
import { useAuthorBySlug, useArticlesByAuthor } from "@/hooks/usePublicArticles";
import type { Author, Article } from "@/mocks/articles";
import AdSlotDisplay from "@/components/feature/AdSlotDisplay";
import NewsletterBox from "@/components/feature/NewsletterBox";

const socialIcons: Record<string, string> = {
  twitter: "ri-twitter-x-line",
  linkedin: "ri-linkedin-line",
  instagram: "ri-instagram-line",
  github: "ri-github-line",
  facebook: "ri-facebook-line",
  youtube: "ri-youtube-line",
};

const socialLabels: Record<string, string> = {
  twitter: "Twitter/X",
  linkedin: "LinkedIn",
  instagram: "Instagram",
  github: "GitHub",
  facebook: "Facebook",
  youtube: "YouTube",
};

function AuthorArticleCard({ article }: { article: Article }) {
  const wordCount = article.content.split(/\s+/).filter(Boolean).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));
  const isColumnist = article.isColumn || article.categorySlug === "kolumnen";

  return (
    <Link
      to={`/artikel/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-5 p-5 bg-white rounded-xl border border-hj-gray-border hover:border-hj-red/30 transition-colors"
    >
      <div className="w-full sm:w-48 md:w-56 shrink-0 overflow-hidden rounded-lg aspect-[16/10]">
        <img
          src={article.imageUrl}
          alt={article.imageAlt || article.title}
          loading="lazy"
          width={224}
          height={140}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 text-xs font-semibold text-white bg-hj-red rounded uppercase tracking-wide">
            {article.category}
          </span>
          {isColumnist && (
            <span className="px-2 py-0.5 text-xs font-medium text-hj-red bg-hj-red/10 rounded">
              Kolumne
            </span>
          )}
        </div>
        <h3 className="text-lg font-bold text-hj-dark leading-snug group-hover:text-hj-red transition-colors font-heading">
          {article.title}
        </h3>
        {article.subtitle && (
          <p className="text-sm text-hj-gray mt-1 line-clamp-2 font-body">{article.subtitle}</p>
        )}
        <div className="flex items-center gap-3 mt-3 text-xs text-hj-muted font-body">
          <time dateTime={article.publishedAt}>
            {new Date(article.publishedAt).toLocaleDateString("de-CH", {
              weekday: "short",
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </time>
          <span className="w-1 h-1 rounded-full bg-hj-muted"></span>
          <span>{readingTime} Min.</span>
        </div>
      </div>
    </Link>
  );
}

export default function AuthorPage() {
  const { slug } = useParams<{ slug: string }>();
  const { author, loading: authorLoading, error: authorError } = useAuthorBySlug(slug);
  const { articles: authorArticles, loading: articlesLoading } = useArticlesByAuthor(slug);
  const isColumnist = author?.author_type === "kolumnist";

  if (authorLoading) {
    return (
      <div className="min-h-screen bg-white pt-[100px] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-hj-red border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (authorError) {
    return (
      <div className="min-h-screen bg-white pt-[100px] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-hj-dark mb-2 font-heading">Fehler beim Laden</h1>
          <p className="text-hj-gray mb-4 font-body">{authorError}</p>
          <Link to="/" className="text-hj-red hover:underline font-body">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  if (!author) {
    return (
      <div className="min-h-screen bg-white pt-[100px] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-hj-dark mb-2 font-heading">Autor nicht gefunden</h1>
          <p className="text-hj-gray mb-4 font-body">Der angeforderte Autor existiert nicht.</p>
          <Link to="/" className="text-hj-red hover:underline font-body">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-[80px] md:pt-[130px]">
      {/* Author Header */}
      <div className={`${isColumnist ? "bg-hj-navy" : "bg-hj-dark"} text-white py-6 sm:py-8 md:py-16`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-white/60 mb-3 md:mb-6 font-body">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={isColumnist ? "/kolumnisten" : "/"} className="hover:text-white transition-colors">
              {isColumnist ? "Kolumnisten" : "Autoren"}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-white">{author.name}</span>
          </nav>

          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-36 md:h-36 rounded-full overflow-hidden shrink-0 border-3 md:border-4 border-white/20 bg-white/10">
              {author.image ? (
                <img src={author.image} alt={author.name} loading="lazy" width={144} height={144} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/60">
                  <i className="ri-user-line text-3xl md:text-4xl lg:text-5xl"></i>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-1 md:mb-2">
                <h1 className="text-xl sm:text-2xl md:text-4xl font-bold font-heading">{author.name}</h1>
                <span className={`px-2 md:px-3 py-0.5 md:py-1 text-[10px] md:text-xs font-semibold rounded-full ${
                  isColumnist ? "bg-hj-red/20 text-hj-red-light" : "bg-white/10 text-white/80"
                }`}>
                  {isColumnist ? "Kolumnist" : author.author_type === "redaktion" ? "Redaktion" : author.author_type === "editor" ? "Editor" : "Autor"}
                </span>
              </div>
              <p className="text-hj-red-light text-xs md:text-sm font-semibold mt-0.5 md:mt-1 font-body">{author.role}</p>
              <p className="text-white/70 mt-2 md:mt-4 max-w-2xl leading-relaxed font-body text-sm md:text-base lg:text-lg">
                {author.longBio || author.shortBio}
              </p>

              <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-3 md:mt-5 text-xs md:text-sm text-white/50 font-body">
                <span className="flex items-center gap-1.5">
                  <i className="ri-article-line"></i>
                  {authorArticles.length} {isColumnist ? "Kolumnen" : "Artikel"}
                </span>
                {author.email && (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-mail-line"></i>
                    {author.email}
                  </span>
                )}
              </div>

              {author.socialLinks.length > 0 && (
                <div className="flex items-center gap-2 mt-3 md:mt-5">
                  {author.socialLinks.map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
                      title={socialLabels[link.platform] || link.platform}
                    >
                      <i className={`${socialIcons[link.platform] || "ri-link"} text-xs md:text-sm`}></i>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Articles */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1">
            <h2 className="text-xl md:text-2xl font-bold text-hj-dark mb-6 font-heading">
              {isColumnist ? "Kolumnen" : "Artikel"} von {author.name}
            </h2>

            {articlesLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="h-32 rounded-xl bg-hj-gray-light animate-pulse"></div>
                ))}
              </div>
            ) : authorArticles.length > 0 ? (
              <div className="space-y-6">
                {authorArticles.map((article) => (
                  <AuthorArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-hj-gray-light rounded-xl">
                <i className="ri-article-line text-4xl text-hj-gray mb-3"></i>
                <p className="text-hj-gray font-body">Noch keine {isColumnist ? "Kolumnen" : "Artikel"} veröffentlicht.</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="w-full lg:w-72 shrink-0 space-y-6">
            <div className="bg-white rounded-xl border border-hj-gray-border p-5">
              <h3 className="text-sm font-bold uppercase tracking-wider text-hj-dark mb-4 font-body">
                Über {author.name.split(" ")[0]}
              </h3>
              <p className="text-sm text-hj-gray leading-relaxed font-body">{author.shortBio}</p>
              <div className="mt-4 pt-4 border-t border-hj-gray-border">
                <div className="flex items-center justify-between text-sm font-body">
                  <span className="text-hj-gray">Rolle</span>
                  <span className="font-medium text-hj-dark">{author.role}</span>
                </div>
                <div className="flex items-center justify-between text-sm font-body mt-2">
                  <span className="text-hj-gray">Artikel</span>
                  <span className="font-medium text-hj-dark">{authorArticles.length}</span>
                </div>
                <div className="flex items-center justify-between text-sm font-body mt-2">
                  <span className="text-hj-gray">Status</span>
                  <span className={`font-medium ${author.isActive ? "text-emerald-600" : "text-gray-500"}`}>
                    {author.isActive ? "Aktiv" : "Inaktiv"}
                  </span>
                </div>
              </div>
            </div>

            {author.socialLinks.length > 0 && (
              <div className="bg-white rounded-xl border border-hj-gray-border p-5">
                <h3 className="text-sm font-bold uppercase tracking-wider text-hj-dark mb-4 font-body">
                  Folgen Sie {author.name.split(" ")[0]}
                </h3>
                <div className="space-y-2">
                  {author.socialLinks.map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-hj-gray-light transition-colors"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-gray-light text-hj-gray">
                        <i className={`${socialIcons[link.platform] || "ri-link"} text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-hj-dark font-body">{socialLabels[link.platform] || link.platform}</p>
                        <p className="text-xs text-hj-gray truncate max-w-[180px]">{link.url}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-hj-dark rounded-xl p-5 text-white">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3 font-body">Kontakt</h3>
              <p className="text-sm text-white/70 font-body mb-3">
                Möchten Sie {author.name} kontaktieren oder ein Interview anfragen?
              </p>
              {author.email ? (
                <a
                  href={`mailto:${author.email}`}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-hj-dark text-sm font-medium rounded-md hover:bg-white/90 transition-colors"
                >
                  <i className="ri-mail-line"></i>
                  E-Mail senden
                </a>
              ) : (
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-hj-dark text-sm font-medium rounded-md hover:bg-white/90 transition-colors"
                >
                  <i className="ri-mail-line"></i>
                  Kontakt aufnehmen
                </Link>
              )}
            </div>

            <div className="min-h-[250px]">
              <AdSlotDisplay position="sidebar_top" />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}